import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import {
  parseUrlHelper,
  Box,
  Button,
  Input,
  Modal,
  ModalProps,
  Multiselect,
  Select,
  SelectItem,
  Stack,
  Title,
  useModals,
} from "@skailer-com/uikit";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import useEmployeeSearchInline, {
  useEmployeesByIds,
} from "@/queries/employees/use-employees.query.ts";
import { useDepartmentsTree } from "@/queries/use-departments.query.ts";
import { useDivisionsByIds, useDivisionsSearchInline } from "@/queries/use-divisitions.query.ts";
import { useLocationsByIds, useLocationsSearchInline } from "@/queries/use-locations.query.ts";
import { useJobProfiles } from "@/queries/use-job-profiles.query.ts";
import { useJobRolesSearchInline } from "@/queries/use-job-roles.query.ts";
import { useEntityReferenceSelect } from "@/hooks/use-entity-reference-options.hook.ts";
import { isDateFilterEmpty } from "@/components/date-filter/date-filter.utils.ts";
import { DateRangeFilter } from "@/components/date-filter/DateRangeFilter.tsx";
import { PeriodFilter } from "@/components/date-filter/PeriodFilter.tsx";
import {
  ApiCFTableFieldFilter,
  ApiCFTableRowFilter,
  ApiField,
  ApiFieldValueTypes,
} from "@/types/custom-field.types.ts";
import { getLifecycleStatusSelectItems } from "@/types/employees/employee.types.ts";
import { unionBy } from "lodash";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

function toDateOrUndefined(value: unknown): Date | undefined {
  if (value instanceof Date) return isNaN(value.getTime()) ? undefined : value;
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
}

function coerceFieldFilterDates(filters: ApiCFTableFieldFilter[]): ApiCFTableFieldFilter[] {
  return filters.map((f) => ({
    ...f,
    datetime_from: toDateOrUndefined(f.datetime_from),
    datetime_to: toDateOrUndefined(f.datetime_to),
  }));
}

export interface EmployeeCustomTableRowsFilterModalProps extends ModalProps {
  filter: ApiCFTableRowFilter;
  isHistoricalTable: boolean;
  tableFields: ApiField[];
}

interface FormValues {
  employee_ids: number[];
  job_profile_ids: number[];
  job_role_ids: number[];
  department_ids: number[];
  division_ids: number[];
  location_ids: number[];
  employee_lifecycle_statuses: string[];
  date_effective_from: Date | undefined;
  date_effective_to: Date | undefined;
  field_filters: ApiCFTableFieldFilter[];
}

export function EmployeeCustomTableRowsFilterModal({
  filter,
  isHistoricalTable,
  tableFields,
  ...props
}: EmployeeCustomTableRowsFilterModalProps) {
  const { close } = useModals();
  const { id } = props;
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const [searchParams, setSearchParams] = useSearchParams();

  const employeeSearch = useEmployeeSearchInline(true);
  const { options: jobProfileOptions, isLoading: isLoadingJobProfiles } = useJobProfiles();
  const jobRoles = useJobRolesSearchInline(filter.job_role_ids ?? []);
  const divisions = useDivisionsSearchInline(true);
  const locations = useLocationsSearchInline(true);
  const departmentsAll = useDepartmentsTree(true);

  const { data: employeesLoaded } = useEmployeesByIds(filter.employee_ids ?? [], true);
  const { data: divisionsLoaded } = useDivisionsByIds({
    ids: filter.division_ids ?? [],
    enabled: true,
  });
  const { data: locationsLoaded } = useLocationsByIds({
    ids: filter.location_ids ?? [],
    enabled: true,
  });

  const lifecycleOptions = useMemo(() => getLifecycleStatusSelectItems(), []);

  const employeeOptions = useMemo<SelectItem[]>(() => {
    const loaded: SelectItem[] = (employeesLoaded ?? []).map((e) => ({
      id: e.id,
      title: renderEmployeeName(e),
    }));
    return unionBy<SelectItem>(loaded, employeeSearch.options, "id");
  }, [employeesLoaded, employeeSearch.options, renderEmployeeName]);

  const { handleSubmit, control, reset } = useForm<FormValues>({
    defaultValues: {
      employee_ids: filter.employee_ids ?? [],
      job_profile_ids: filter.job_profile_ids ?? [],
      job_role_ids: filter.job_role_ids ?? [],
      department_ids: filter.department_ids ?? [],
      division_ids: filter.division_ids ?? [],
      location_ids: filter.location_ids ?? [],
      employee_lifecycle_statuses: (filter.employee_lifecycle_statuses ?? []) as string[],
      date_effective_from: filter.date_effective_from
        ? new Date(filter.date_effective_from)
        : undefined,
      date_effective_to: filter.date_effective_to ? new Date(filter.date_effective_to) : undefined,
      field_filters: coerceFieldFilterDates(filter.field_filters ?? []),
    },
  });

  const onSubmit = handleSubmit((values) => {
    const newParams = new URLSearchParams(searchParams);

    const setOrDelete = (key: string, value: string | undefined) => {
      if (value && value.length > 0) newParams.set(key, value);
      else newParams.delete(key);
    };

    setOrDelete(
      "employee_ids",
      values.employee_ids.length > 0 ? values.employee_ids.join(",") : undefined
    );
    setOrDelete(
      "job_profile_ids",
      values.job_profile_ids.length > 0 ? values.job_profile_ids.join(",") : undefined
    );
    setOrDelete(
      "job_role_ids",
      values.job_role_ids.length > 0 ? values.job_role_ids.join(",") : undefined
    );
    setOrDelete(
      "department_ids",
      values.department_ids.length > 0 ? values.department_ids.join(",") : undefined
    );
    setOrDelete(
      "division_ids",
      values.division_ids.length > 0 ? values.division_ids.join(",") : undefined
    );
    setOrDelete(
      "location_ids",
      values.location_ids.length > 0 ? values.location_ids.join(",") : undefined
    );
    newParams.set("employee_lifecycle_statuses", values.employee_lifecycle_statuses.join(","));
    setOrDelete(
      "date_effective_from",
      values.date_effective_from ? parseUrlHelper.formatDate(values.date_effective_from) : undefined
    );
    setOrDelete(
      "date_effective_to",
      values.date_effective_to ? parseUrlHelper.formatDate(values.date_effective_to) : undefined
    );

    const trimmedFieldFilters = values.field_filters.filter((f) => !isFieldFilterEmpty(f));
    setOrDelete(
      "field_filters",
      trimmedFieldFilters.length > 0 ? JSON.stringify(trimmedFieldFilters) : undefined
    );

    newParams.set("page", "1");
    setSearchParams(newParams);
    close(id);
  });

  const handleClearFilters = () => {
    reset({
      employee_ids: [],
      job_profile_ids: [],
      job_role_ids: [],
      department_ids: [],
      division_ids: [],
      location_ids: [],
      employee_lifecycle_statuses: [],
      date_effective_from: undefined,
      date_effective_to: undefined,
      field_filters: [],
    });

    const newParams = new URLSearchParams(searchParams);
    [
      "employee_ids",
      "job_profile_ids",
      "job_role_ids",
      "department_ids",
      "division_ids",
      "location_ids",
      "date_effective_from",
      "date_effective_to",
      "field_filters",
    ].forEach((k) => newParams.delete(k));
    newParams.set("employee_lifecycle_statuses", "");
    newParams.set("page", "1");
    setSearchParams(newParams);
    close(id);
  };

  return (
    <Modal
      {...props}
      variant={"side"}
      title={t("common:filter")}
      layout={"stretch"}
      withCloser
      closeByEscEnabled
      actions={
        <Stack gap={"sm"}>
          <Button size={"lg"} className={"w-full"} type={"submit"} onClick={onSubmit}>
            {t("common:show_results")}
          </Button>
          <Button size={"md"} variant={"plain"} onClick={handleClearFilters}>
            {t("common:filter_clear")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Title
          header={t("common:employee_filter")}
          size={"xs"}
          paddingBottom={true}
          paddingTop={true}
        />

        <Controller
          render={({ field: { value, onChange } }) => (
            <Multiselect
              label={t("core:employees")}
              emptyTitle={t("common:select")}
              isMulti
              isClearable
              showAvatar
              showCaption
              searchable
              loadOptionsOnOpen
              isLoading={employeeSearch.isLoading}
              options={employeeOptions}
              searchOptions={employeeSearch.options}
              onSearchOptions={employeeSearch.search}
              values={value}
              onChange={onChange}
            />
          )}
          control={control}
          name={"employee_ids"}
        />

        <Controller
          render={({ field: { value, onChange } }) => (
            <Multiselect
              label={t("core:job_profile")}
              emptyTitle={t("common:select")}
              isMulti
              isClearable
              searchable
              isLoading={isLoadingJobProfiles}
              options={jobProfileOptions}
              showCaption
              captionPerItem
              values={value}
              onChange={onChange}
            />
          )}
          control={control}
          name={"job_profile_ids"}
        />

        <Controller
          render={({ field: { value, onChange } }) => (
            <Multiselect
              label={t("core:job_role")}
              emptyTitle={t("common:select")}
              isMulti
              isClearable
              searchable
              isLoading={jobRoles.isLoading}
              options={jobRoles.options}
              searchOptions={jobRoles.options}
              onSearchOptions={jobRoles.search}
              loadOptionsOnOpen
              values={value}
              onChange={onChange}
            />
          )}
          control={control}
          name={"job_role_ids"}
        />

        <Controller
          render={({ field: { value, onChange } }) => (
            <Multiselect
              label={t("core:department")}
              emptyTitle={t("common:select")}
              isMulti
              isClearable
              searchable
              isLoading={departmentsAll.isLoading}
              options={(departmentsAll.data ?? []) as SelectItem[]}
              values={value}
              onChange={onChange}
            />
          )}
          control={control}
          name={"department_ids"}
        />

        <Controller
          render={({ field: { value, onChange } }) => (
            <Multiselect
              label={t("core:division")}
              emptyTitle={t("common:select")}
              isMulti
              isClearable
              searchable
              loadOptionsOnOpen
              isLoading={divisions.isLoading}
              options={(divisionsLoaded ?? []) as SelectItem[]}
              searchOptions={divisions.options}
              onSearchOptions={divisions.search}
              values={value}
              onChange={onChange}
            />
          )}
          control={control}
          name={"division_ids"}
        />

        <Controller
          render={({ field: { value, onChange } }) => (
            <Multiselect
              label={t("common:location")}
              emptyTitle={t("common:select")}
              isMulti
              isClearable
              searchable
              loadOptionsOnOpen
              isLoading={locations.isLoading}
              options={(locationsLoaded ?? []) as SelectItem[]}
              searchOptions={locations.options}
              onSearchOptions={locations.search}
              values={value}
              onChange={onChange}
            />
          )}
          control={control}
          name={"location_ids"}
        />

        <Controller
          render={({ field: { value, onChange } }) => (
            <Multiselect
              label={t("core:lifecycle_status")}
              emptyTitle={t("common:select")}
              isMulti
              isClearable
              options={lifecycleOptions}
              values={value}
              onChange={onChange}
            />
          )}
          control={control}
          name={"employee_lifecycle_statuses"}
        />

        {isHistoricalTable && (
          <Controller
            control={control}
            name={"date_effective_from"}
            render={({ field: fromField }) => (
              <Controller
                control={control}
                name={"date_effective_to"}
                render={({ field: toField }) => (
                  <DateRangeFilter
                    title={t("common:date")}
                    from={fromField.value ?? undefined}
                    to={toField.value ?? undefined}
                    onChange={({ from, to }) => {
                      fromField.onChange(from ?? null);
                      toField.onChange(to ?? null);
                    }}
                  />
                )}
              />
            )}
          />
        )}

        {tableFields.some((f) => isEditableType(f.field_type)) && (
          <>
            <Title header={t("custom_fields:fields")} size={"xs"} paddingTop={true} />
            <Controller
              control={control}
              name={"field_filters"}
              render={({ field: { value, onChange } }) => (
                <Stack gap={"xs"}>
                  {tableFields
                    .filter((f) => isEditableType(f.field_type))
                    .map((field) => (
                      <FieldEditor
                        key={field.slug}
                        field={field}
                        current={value.find((f) => f.slug === field.slug)}
                        onUpdate={(updater) => {
                          const existing = value.find((f) => f.slug === field.slug);
                          const base: ApiCFTableFieldFilter = existing ?? {
                            slug: field.slug,
                            field_type: field.field_type,
                          };
                          const next = updater(base);
                          const without = value.filter((f) => f.slug !== field.slug);
                          onChange([...without, next]);
                        }}
                      />
                    ))}
                </Stack>
              )}
            />
          </>
        )}
      </form>
    </Modal>
  );
}

function isEditableType(type: ApiFieldValueTypes): boolean {
  return (
    type === ApiFieldValueTypes.text ||
    type === ApiFieldValueTypes.textarea ||
    type === ApiFieldValueTypes.email ||
    type === ApiFieldValueTypes.phone ||
    type === ApiFieldValueTypes.url ||
    type === ApiFieldValueTypes.number ||
    type === ApiFieldValueTypes.percentage ||
    type === ApiFieldValueTypes.currency ||
    type === ApiFieldValueTypes.date ||
    type === ApiFieldValueTypes.datetime ||
    type === ApiFieldValueTypes.switch ||
    type === ApiFieldValueTypes.select ||
    type === ApiFieldValueTypes.employee_reference ||
    type === ApiFieldValueTypes.entity_reference
  );
}

function isFieldFilterEmpty(f: ApiCFTableFieldFilter): boolean {
  return (
    f.text == null &&
    f.number_from == null &&
    f.number_to == null &&
    isDateFilterEmpty(f.date_filter) &&
    f.datetime_from == null &&
    f.datetime_to == null &&
    f.switch == null &&
    (!f.option_ids || f.option_ids.length === 0) &&
    (!f.employee_ids || f.employee_ids.length === 0) &&
    (!f.reference_ids || f.reference_ids.length === 0)
  );
}

interface FieldEditorProps {
  field: ApiField;
  current: ApiCFTableFieldFilter | undefined;
  onUpdate: (updater: (current: ApiCFTableFieldFilter) => ApiCFTableFieldFilter) => void;
}

function FieldEditor({ field, current, onUpdate }: FieldEditorProps) {
  const { t } = useTranslation();

  switch (field.field_type) {
    case ApiFieldValueTypes.text:
    case ApiFieldValueTypes.textarea:
    case ApiFieldValueTypes.email:
    case ApiFieldValueTypes.phone:
    case ApiFieldValueTypes.url:
      return (
        <Input
          label={field.title}
          type={"text"}
          value={current?.text ?? ""}
          onChange={(e) =>
            onUpdate((cur) => ({
              ...cur,
              text: e.currentTarget.value || undefined,
            }))
          }
        />
      );

    case ApiFieldValueTypes.number:
    case ApiFieldValueTypes.percentage:
    case ApiFieldValueTypes.currency:
      return (
        <>
          <Title header={field.title} size={"xs"} variant="secondary" paddingTop={true} />
          <Box className={"grid grid-cols-2 gap-sm"}>
            <Input
              className="-mt-2"
              label={t("common:number_from_short")}
              type={"number"}
              value={current?.number_from != null ? String(current.number_from) : ""}
              onChange={(e) => {
                const v = e.currentTarget.value;
                onUpdate((cur) => ({
                  ...cur,
                  number_from: v === "" ? undefined : Number(v),
                }));
              }}
            />
            <Input
              className="-mt-2"
              label={t("common:number_to_short")}
              type={"number"}
              value={current?.number_to != null ? String(current.number_to) : ""}
              onChange={(e) => {
                const v = e.currentTarget.value;
                onUpdate((cur) => ({
                  ...cur,
                  number_to: v === "" ? undefined : Number(v),
                }));
              }}
            />
          </Box>
        </>
      );

    case ApiFieldValueTypes.date:
      return (
        <PeriodFilter
          title={field.title}
          value={current?.date_filter}
          onChange={(date_filter) => onUpdate((cur) => ({ ...cur, date_filter }))}
        />
      );

    case ApiFieldValueTypes.datetime:
      return (
        <>
          <Title
            header={field.title}
            caption={t("common:pick_a_date_range")}
            size={"xs"}
            variant="secondary"
          />
          <Box className={"grid grid-cols-2 gap-sm"}>
            <Datepicker
              isClearable
              value={toDateOrUndefined(current?.datetime_from)}
              label={t("common:date_from_short")}
              onChange={(date: Date | null) =>
                onUpdate((cur) => ({
                  ...cur,
                  datetime_from: date ?? undefined,
                }))
              }
            />
            <Datepicker
              isClearable
              value={toDateOrUndefined(current?.datetime_to)}
              label={t("common:date_to_short")}
              onChange={(date: Date | null) =>
                onUpdate((cur) => ({
                  ...cur,
                  datetime_to: date ?? undefined,
                }))
              }
            />
          </Box>
        </>
      );

    case ApiFieldValueTypes.switch: {
      const switchFilterOptions: SelectItem[] = [
        { id: "all", title: t("common:all") },
        { id: "true", title: t("common:yes") },
        { id: "false", title: t("common:no") },
      ];

      return (
        <Select
          label={field.title}
          emptyTitle={t("common:all")}
          options={switchFilterOptions}
          value={current?.switch == null ? "all" : current.switch ? "true" : "false"}
          onChange={(nextValue) =>
            onUpdate((cur) => ({
              ...cur,
              switch: nextValue == null || nextValue === "all" ? undefined : nextValue === "true",
            }))
          }
        />
      );
    }

    case ApiFieldValueTypes.select:
      return (
        <Multiselect
          label={field.title}
          emptyTitle={t("common:select")}
          isMulti
          isClearable
          options={(field.options ?? []).map((opt) => ({
            id: opt.id as number,
            title: opt.title,
          }))}
          values={current?.option_ids ?? []}
          onChange={(values) => {
            const ids = (values as (number | undefined)[]).filter(
              (v): v is number => typeof v === "number"
            );
            onUpdate((cur) => ({
              ...cur,
              option_ids: ids.length > 0 ? ids : undefined,
            }));
          }}
        />
      );

    case ApiFieldValueTypes.employee_reference:
      return <EmployeeReferenceFieldEditor field={field} current={current} onUpdate={onUpdate} />;

    case ApiFieldValueTypes.entity_reference:
      return <EntityReferenceFieldEditor field={field} current={current} onUpdate={onUpdate} />;

    default:
      return null;
  }
}

function EntityReferenceFieldEditor({ field, current, onUpdate }: FieldEditorProps) {
  const { t } = useTranslation();
  const selectedIds = useMemo(() => current?.reference_ids ?? [], [current?.reference_ids]);
  const { options, searchOptions, onSearchOptions, loadOptionsOnOpen, isLoading } =
    useEntityReferenceSelect(field.reference_entity, selectedIds);

  return (
    <Multiselect
      label={field.title}
      emptyTitle={t("common:select")}
      isMulti
      isClearable
      searchable
      isLoading={isLoading}
      options={options}
      searchOptions={searchOptions}
      onSearchOptions={onSearchOptions}
      loadOptionsOnOpen={loadOptionsOnOpen}
      values={selectedIds}
      onChange={(values) => {
        const ids = (values as (number | undefined)[]).filter(
          (value): value is number => typeof value === "number"
        );
        onUpdate((cur) => ({
          ...cur,
          reference_ids: ids.length > 0 ? ids : undefined,
        }));
      }}
    />
  );
}

function EmployeeReferenceFieldEditor({ field, current, onUpdate }: FieldEditorProps) {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const selectedIds = current?.employee_ids ?? [];
  const search = useEmployeeSearchInline(true);
  const { data: selectedEmployees } = useEmployeesByIds(selectedIds, selectedIds.length > 0);

  const options = useMemo<SelectItem[]>(() => {
    const selected: SelectItem[] = (selectedEmployees ?? []).map((e) => ({
      id: e.id,
      title: renderEmployeeName(e),
    }));
    return unionBy<SelectItem>(selected, search.options, "id");
  }, [selectedEmployees, search.options, renderEmployeeName]);

  return (
    <Multiselect
      label={field.title}
      emptyTitle={t("common:select")}
      isMulti
      isClearable
      showAvatar
      showCaption
      searchable
      loadOptionsOnOpen
      isLoading={search.isLoading}
      options={options}
      searchOptions={search.options}
      onSearchOptions={search.search}
      values={selectedIds}
      onChange={(values) => {
        const ids = (values as (number | undefined)[]).filter(
          (v): v is number => typeof v === "number"
        );
        onUpdate((cur) => ({
          ...cur,
          employee_ids: ids.length > 0 ? ids : undefined,
        }));
      }}
    />
  );
}
